import { Box, Button, Center, Grid, Select, Stack, Text } from '@mantine/core';
import { IconBuildingEstate, IconCircleCheck, IconClockHour4, IconWorld } from '@tabler/icons-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type Page } from '../api/client';
import type { PropertyRow, StatsOverview } from '../api/types';
import { BarsCard, dayRange, Kpi, PROPERTY_BAR, statusBars, toneOfBar, TrendCard } from '../components/charts';
import { defaultRange, OverviewFilters, type RangeFilter } from '../components/OverviewFilters';
import { PageActions } from '../components/page-actions';
import { useDebounced } from '../components/useDebounced';
import { nameOf, tr } from '../lib/i18n';
import { fmtDate, fmtNum, PROPERTY_STATUS, SOURCE } from '../lib/labels';
import { DataTable, tableDefaults } from '../ui/DataTable';
import { Code, ErrorState, Loading, Panel, SearchInput, StatusBadge, Toolbar } from '../ui/kit';

const ENTITY = 'PROPERTY';

export function PropertiesOverviewPage() {
  const [range, setRange] = useState(defaultRange());
  const stats = useQuery({
    queryKey: ['stats', range],
    queryFn: () => api<StatsOverview>('/stats/overview', { query: { from: range.from, to: range.to, organizationId: range.organizationId } }),
  });
  const s = stats.data;
  const mine = <T extends { entity: string }>(rows: T[] = []) => rows.filter((r) => r.entity === ENTITY);
  const byStatus = mine(s?.byStatus);
  const count = (st: string) => byStatus.filter((r) => r.status === st).reduce((a, r) => a + r.total, 0);
  const publicCount = mine(s?.bySource).filter((r) => r.source === 'PUBLIC_LINK').reduce((a, r) => a + r.total, 0);
  const days = s ? dayRange(s.range.from, s.range.to) : [];
  const perDay = new Map(mine(s?.series).map((r) => [r.day, r.total]));

  return (
    <Stack gap="lg">
      <PageActions><OverviewFilters value={range} onChange={setRange} /></PageActions>
      {stats.isLoading ? <Loading /> : stats.isError ? <ErrorState error={stats.error} onRetry={() => stats.refetch()} /> : s && (
        <>
          <Grid gap="lg">
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('عقارات جديدة', 'New properties')} hint={tr('في الفترة المختارة', 'In the selected period')} value={fmtNum(s.totals.properties)} icon={<IconBuildingEstate size={16} />} /></Grid.Col>
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('متاحة', 'Available')} value={fmtNum(count('AVAILABLE'))} tone="teal" icon={<IconCircleCheck size={16} />} /></Grid.Col>
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('بانتظار المراجعة', 'Pending review')} value={fmtNum(count('PENDING_REVIEW'))} tone="orange" icon={<IconClockHour4 size={16} />} /></Grid.Col>
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('من الرابط العام', 'From public link')} value={fmtNum(publicCount)} tone="blue" icon={<IconWorld size={16} />} /></Grid.Col>
            <Grid.Col span={12}>
              <TrendCard title={tr('العقارات الجديدة يومياً', 'New properties per day')} days={days} series={[{ label: tr('العقارات', 'Properties'), data: days.map((d) => perDay.get(d) ?? 0) }]} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><BarsCard title={tr('حسب الحالة', 'By status')} rows={statusBars(byStatus, PROPERTY_STATUS, PROPERTY_BAR)} /></Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><BarsCard title={tr('حسب نوع الطلب', 'By request type')} rows={mine(s.byRequestType).map((r) => ({ label: nameOf(r), value: r.total }))} /></Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><BarsCard title={tr('حسب نوع العقار', 'By property type')} rows={mine(s.byPropertyType).map((r) => ({ label: nameOf(r), value: r.total }))} /></Grid.Col>
          </Grid>
          <Text size="xs" c="dimmed" mt={-8}>{tr('الأرقام من الإحصاءات اليومية وتُحدَّث كل 5 دقائق. الحالة هي الحالة الحالية للعقارات المضافة في الفترة.', 'Figures come from daily statistics and refresh every 5 minutes. Status is the current status of properties added in the period.')}</Text>
        </>
      )}
      <PropertiesTable range={range} />
    </Stack>
  );
}

function PropertiesTable({ range }: { range: RangeFilter }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const dq = useDebounced(q);
  const list = useInfiniteQuery({
    queryKey: ['platform-properties', range, dq, status],
    queryFn: ({ pageParam }) => api<Page<PropertyRow>>('/properties', { query: { cursor: pageParam, limit: 25, q: dq, status, organizationId: range.organizationId, createdFrom: range.from, createdTo: range.to } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Panel flush title={tr('العقارات', 'Properties')} description={tr('كل العقارات المضافة في الفترة عبر كل الشركات', 'Every property added in the period, across companies')}>
      <Toolbar>
        <SearchInput value={q} onChange={(e) => setQ(e.currentTarget.value)} placeholder={tr('ابحث بالمرجع أو المالك أو الهاتف…', 'Search reference, owner or phone…')} w={{ base: '100%', sm: 300 }} />
        <Select w={{ base: '100%', sm: 180 }} value={status} onChange={(v) => setStatus(v ?? '')}
          data={[{ value: '', label: tr('كل الحالات', 'All statuses') }, ...Object.entries(PROPERTY_STATUS).map(([value, label]) => ({ value, label }))]} />
      </Toolbar>
      {list.isError ? <Box p="md"><ErrorState error={list.error} onRetry={() => list.refetch()} /></Box> : (
        <DataTable<PropertyRow>
          {...(tableDefaults as object)}
          minHeight={rows.length ? undefined : 240}
          fetching={list.isLoading}
          records={rows}
          noRecordsText={dq || status ? tr('لا توجد عقارات مطابقة', 'No matching properties') : tr('لا توجد عقارات في هذه الفترة', 'No properties in this period')}
          columns={[
            { accessor: 'ref_no', title: tr('المرجع', 'Reference'), noWrap: true, render: (r) => <Code>{r.ref_no}</Code> },
            { accessor: 'organization_name', title: tr('الشركة', 'Company'), render: (r) => <Text size="sm" fw={500}>{r.organization_name}</Text> },
            {
              accessor: 'property_type_name',
              title: tr('العقار', 'Property'),
              render: (r) => (
                <Box>
                  <Text size="sm" lh={1.35}>{r.property_type_name}</Text>
                  <Text size="xs" c="dimmed" lh={1.35}>{r.request_type_name}</Text>
                </Box>
              ),
            },
            { accessor: 'location', title: tr('الموقع', 'Location'), render: (r) => <Text size="sm" c={r.governorate_name ? undefined : 'dimmed'}>{[r.governorate_name, r.area_name, r.neighborhood_name].filter(Boolean).join(' / ') || '—'}</Text> },
            { accessor: 'price', title: tr('السعر', 'Price'), noWrap: true, render: (r) => r.price ? <Text size="sm" className="pf-num">{fmtNum(r.price)} <Text span size="xs" c="dimmed">{r.currency}</Text></Text> : <Text c="dimmed">—</Text> },
            {
              accessor: 'owner_name',
              title: tr('المالك', 'Owner'),
              render: (r) => (
                <Box>
                  <Text size="sm" lh={1.35}>{r.owner_name}</Text>
                  <Text size="xs" c="dimmed" lh={1.35} dir="ltr" ta="start" className="pf-num">{r.owner_phone}</Text>
                </Box>
              ),
            },
            { accessor: 'status', title: tr('الحالة', 'Status'), noWrap: true, render: (r) => <StatusBadge tone={toneOfBar(PROPERTY_BAR[r.status])}>{PROPERTY_STATUS[r.status] ?? r.status}</StatusBadge> },
            {
              accessor: 'created_at',
              title: tr('الإضافة', 'Added'),
              render: (r) => (
                <Box>
                  <Text size="sm" lh={1.35} style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</Text>
                  <Text size="xs" c="dimmed" lh={1.35}>{r.created_by_name ?? tr('عميل', 'Client')} · {SOURCE[r.source]}</Text>
                </Box>
              ),
            },
          ]}
        />
      )}
      {list.hasNextPage && (
        <Center p="sm" style={{ borderTop: '1px solid var(--pf-line)' }}>
          <Button variant="default" onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>{tr('عرض المزيد', 'Show more')}</Button>
        </Center>
      )}
    </Panel>
  );
}
